import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles, Wallet, Palette, Clock, ShieldCheck, Zap } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { useI18n } from "../i18n";

const HERO_IMG = "https://images.unsplash.com/photo-1758523418383-21d8e2b68bff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxjaGVmJTIwdGFibGV0JTIwa2l0Y2hlbiUyMG1vZGVybnxlbnwwfHx8fDE3Nzg1ODI1MzB8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-deep">
      <Nav variant="marketing" />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="absolute top-24 -left-32 w-[420px] h-[420px] rounded-full bg-[#7C3AED] opacity-20 blur-[100px] pointer-events-none" />
        <div className="absolute top-40 -right-24 w-[380px] h-[380px] rounded-full bg-[#00E5FF] opacity-15 blur-[100px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 chip chip-cyan mb-6">
              <Sparkles className="w-3 h-3" />
              {t.landing.tag} · {t.landing.trial}
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              <span className="block">{t.landing.heroTitle1}</span>
              <span className="block gradient-text-neon">{t.landing.heroTitle2}</span>
              <span className="block text-neon-yellow text-glow-yellow text-3xl sm:text-4xl lg:text-5xl mt-3 font-mono tracking-tight">{t.landing.heroAccent}</span>
            </h1>
            <p className="mt-7 text-lg text-zinc-400 max-w-xl leading-relaxed">{t.landing.heroSub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" className="btn-primary" data-testid="hero-cta-start">
                {t.landing.cta} <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#pricing" className="btn-outline" data-testid="hero-cta-pricing">{t.landing.ctaSecondary}</a>
            </div>
            <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-2xl">
              {[
                { c: "chip-cyan", txt: t.landing.bullet1 },
                { c: "chip-violet", txt: t.landing.bullet2 },
                { c: "chip-yellow", txt: t.landing.bullet3 },
              ].map((b, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                  <Check className={`w-4 h-4 mt-0.5 shrink-0 ${i === 0 ? "text-neon-cyan" : i === 1 ? "text-neon-violet" : "text-neon-yellow"}`} />
                  <span>{b.txt}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden glow-violet">
              <img src={HERO_IMG} alt="chef" className="w-full h-[480px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#06061a]/85 via-transparent to-[#7C3AED]/35" />
              <div className="absolute top-5 left-5 card-deep px-4 py-3 glow-cyan">
                <div className="text-[9px] uppercase tracking-[0.25em] text-neon-cyan font-mono">LIVE ORDER</div>
                <div className="font-heading font-black text-2xl mt-1">€34.80</div>
                <div className="text-[10px] text-zinc-400 font-mono">6% RENOVA · 94% RISTORATORE</div>
              </div>
              <div className="absolute bottom-5 right-5 bg-neon-yellow rounded-2xl px-4 py-3 text-[#06061a]">
                <div className="text-[9px] uppercase tracking-[0.25em] font-mono">TEMPO</div>
                <div className="font-heading font-black text-xl">~25 MIN</div>
              </div>
              <div className="absolute bottom-5 left-5 chip chip-violet">
                <Zap className="w-3 h-3" /> URGENZA +€2
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="bg-deep-surface border-y border-line">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-12 gap-6 items-end mb-12">
            <div className="lg:col-span-7">
              <div className="chip chip-violet">{t.nav.features}</div>
              <h2 className="font-heading text-4xl sm:text-5xl tracking-tight mt-3">{t.landing.featuresTitle}</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: <Palette className="w-5 h-5" />, title: t.landing.f1Title, desc: t.landing.f1Desc, color: "cyan" },
              { icon: <ShieldCheck className="w-5 h-5" />, title: t.landing.f2Title, desc: t.landing.f2Desc, color: "violet" },
              { icon: <Clock className="w-5 h-5" />, title: t.landing.f3Title, desc: t.landing.f3Desc, color: "yellow" },
              { icon: <Wallet className="w-5 h-5" />, title: t.landing.f4Title, desc: t.landing.f4Desc, color: "magenta" },
            ].map((f, i) => (
              <div key={i} className="card-neon p-6" data-testid={`feature-${i}`}>
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-5 ${
                  f.color === "cyan" ? "bg-[#00E5FF] text-[#06061a]" :
                  f.color === "violet" ? "bg-[#7C3AED] text-white" :
                  f.color === "yellow" ? "bg-[#FFD600] text-[#06061a]" :
                  "bg-[#FF2EC8] text-white"
                }`}>{f.icon}</div>
                <h3 className="font-heading font-semibold text-lg">{f.title}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-14">
          <div className="chip chip-yellow inline-flex">{t.nav.pricing}</div>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight mt-4">{t.landing.pricingTitle}</h2>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto">{t.landing.pricingSub}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Basic */}
          <div className="card-neon p-8 relative overflow-hidden" data-testid="pricing-basic">
            <div className="chip chip-cyan inline-flex">{t.landing.basicTitle}</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-heading font-black text-5xl text-neon-cyan text-glow-cyan">{t.landing.basicPrice}</span>
              <span className="text-zinc-500 font-mono text-sm">{t.landing.basicPer}</span>
            </div>
            <p className="text-zinc-400 mt-2 text-sm">{t.landing.basicDesc}</p>
            <ul className="mt-6 space-y-3 text-sm">
              {[t.landing.basicFeat1, t.landing.basicFeat2, t.landing.basicFeat3, t.landing.basicFeat4].map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-zinc-300"><Check className="w-4 h-4 text-neon-cyan" /> {f}</li>
              ))}
            </ul>
            <Link to="/register" className="btn-outline mt-7 w-full justify-center" data-testid="pricing-basic-cta">{t.landing.ctaPlan}</Link>
          </div>

          {/* Pro */}
          <div className="card-neon p-8 relative overflow-hidden glow-violet" data-testid="pricing-pro">
            <div className="absolute top-3 right-3 chip chip-yellow">{t.landing.trial}</div>
            <div className="chip chip-violet inline-flex">{t.landing.proTitle}</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-heading font-black text-5xl gradient-text-neon">{t.landing.proPrice}</span>
              <span className="text-zinc-500 font-mono text-sm">{t.landing.proPer}</span>
            </div>
            <p className="text-zinc-400 mt-2 text-sm">{t.landing.proDesc}</p>
            <ul className="mt-6 space-y-3 text-sm">
              {[t.landing.proFeat1, t.landing.proFeat2, t.landing.proFeat3, t.landing.proFeat4].map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-zinc-300"><Check className="w-4 h-4 text-neon-violet" /> {f}</li>
              ))}
            </ul>
            <Link to="/register" className="btn-primary mt-7 w-full justify-center" data-testid="pricing-pro-cta">{t.landing.ctaPlan}</Link>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center items-center">
          <div className="chip chip-yellow">{t.landing.extraInfo}</div>
          <div className="chip chip-cyan">{t.landing.commissionInfo}</div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-deep-surface border-t border-line">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="chip chip-magenta inline-flex">{t.nav.howItWorks}</div>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight mt-4 max-w-3xl">{t.landing.howTitle}</h2>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: t.landing.step1, d: t.landing.step1Desc, c: "neon-cyan" },
              { n: "02", t: t.landing.step2, d: t.landing.step2Desc, c: "neon-violet" },
              { n: "03", t: t.landing.step3, d: t.landing.step3Desc, c: "neon-yellow" },
            ].map((s, i) => (
              <div key={i} className="card-neon p-7" data-testid={`step-${i}`}>
                <div className={`font-heading font-black text-5xl text-${s.c} text-glow-${s.c.replace("neon-", "")}`}>{s.n}</div>
                <h3 className="font-heading font-semibold text-xl mt-3">{s.t}</h3>
                <p className="text-zinc-400 mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-14">
            <Link to="/register" className="btn-primary text-base" data-testid="bottom-cta">
              {t.landing.cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
